import { supabase } from '../config/supabase';

/**
 * Helper: throw on error OR if RLS silently blocked the operation
 * (Supabase returns { data: null, error: null } when RLS blocks insert + .select())
 */
function assertResult(data, error, operation) {
  if (error) throw error;
  if (!data) {
    throw new Error(
      `${operation} returned null — likely blocked by RLS policy. ` +
      'Check Supabase dashboard → Authentication → Policies.'
    );
  }
  return data;
}

/**
 * Create a user profile in Supabase users table.
 * Called during first-time onboarding after profile steps.
 */
export async function createUserProfile(userData, userId, email) {
  console.log('createUserProfile called:', { userId, email, hasData: !!userData });
  const row = {
    id: userId,
    email,
    phone_number: userData.phone || null,
    first_name: userData.firstName || '',
    last_name: userData.lastName || '',
    zip_code: userData.zipCode || '',
    year_of_birth: userData.yearOfBirth ? Number(userData.yearOfBirth) : null,
    annual_income: userData.salaryRange || null,
    mechanical_level: userData.mechanicalLevel || null,
    primary_use_cases: userData.primaryUseCases || [],
    referral_source: userData.referralSource || null,
    subscription_tier: 'free',
    credits_remaining: 15,
  };

  const { data, error } = await supabase.from('users').upsert({
    ...row,
    is_onboarded: true,
  }, { onConflict: 'id' }).select().single();

  return assertResult(data, error, 'createUserProfile');
}

/**
 * Create a garage for a user.
 * @param {string} userId
 * @param {string} [name='My Garage'] — garage name (synced to Supabase)
 */
export async function createGarage(userId, name = 'My Garage') {
  console.log('createGarage called:', { userId, name });
  const { data, error } = await supabase.from('garages').insert({
    owner_id: userId,
    name,
  }).select().single();

  return assertResult(data, error, 'createGarage');
}

/**
 * Create a vehicle record in Supabase.
 * garageId should be a Supabase UUID (from createGarage), or null.
 */
export async function createVehicle(vehicleData, userId, garageId) {
  console.log('createVehicle called:', { userId, garageId, make: vehicleData.make, model: vehicleData.model });
  const row = {
    user_id: userId,
    year: vehicleData.year ? Number(vehicleData.year) : null,
    make: vehicleData.make || '',
    model: vehicleData.model || '',
    trim: vehicleData.trim || null,
    current_mileage: vehicleData.mileage ? Number(vehicleData.mileage) : null,
    is_tmu: vehicleData.tmu || false,
    estimated_mileage: null,
    condition: vehicleData.condition || null,
    primary_use: Array.isArray(vehicleData.primaryUse)
      ? vehicleData.primaryUse
      : vehicleData.primaryUse ? [vehicleData.primaryUse] : [],
    banner_photo: vehicleData.photo || null,
    distance_units: vehicleData.distance_units || null,
  };
  // Only set garage_id if it's a valid Supabase UUID (not a localStorage ID)
  if (garageId && !String(garageId).startsWith('garage_')) {
    row.garage_id = garageId;
  }
  const { data, error } = await supabase.from('vehicles').insert(row).select().single();

  return assertResult(data, error, 'createVehicle');
}

/**
 * Check if a user profile exists in Supabase.
 * Returns the user row if found, null otherwise.
 */
export async function checkUserExists(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Load all vehicles for a user from Supabase.
 */
export async function loadUserVehicles(userId) {
  const { data, error } = await supabase
    .from('vehicles')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Load all vehicles in a garage via RPC (bypasses RLS).
 * Used when a collaborator accepts a garage invitation.
 */
export async function loadGarageVehicles(garageId) {
  const { data, error } = await supabase.rpc('get_garage_vehicles', { garage_uuid: garageId });
  if (error) throw error;
  return data || [];
}

/**
 * Update a user profile in Supabase.
 * Fields should use Supabase column names (snake_case).
 */
export async function updateUserProfile(userId, fields) {
  const { data, error } = await supabase
    .from('users')
    .update(fields)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Load all garages for a user from Supabase.
 */
export async function loadUserGarages(userId) {
  const { data, error } = await supabase
    .from('garages')
    .select('*')
    .eq('owner_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Load all garages where the user is a collaborator (not owner).
 * Uses RPC to bypass RLS.
 */
export async function loadSharedGarages(userId) {
  const { data, error } = await supabase.rpc('get_shared_garages', { collab_user_id: userId });
  if (error) throw error;
  return data || [];
}

/**
 * Sync vehicle field updates to Supabase.
 * THROWS on failure so callers can queue retries.
 * Skips vehicles with localStorage-style IDs (not yet synced).
 */
export async function updateVehicleInSupabase(vehicleId, fields) {
  if (!vehicleId || String(vehicleId).startsWith('vehicle_')) return;
  const row = {};
  if (fields.photo !== undefined) row.banner_photo = fields.photo;
  if (fields.condition !== undefined) row.condition = fields.condition;
  if (fields.trim !== undefined) row.trim = fields.trim;
  if (fields.primaryUse !== undefined) {
    row.primary_use = Array.isArray(fields.primaryUse) ? fields.primaryUse : [fields.primaryUse];
  }
  if (fields.year !== undefined) row.year = fields.year ? Number(fields.year) : null;
  if (fields.make !== undefined) row.make = fields.make;
  if (fields.model !== undefined) row.model = fields.model;
  if (fields.tmu !== undefined) row.is_tmu = !!fields.tmu;
  if (fields.mileage !== undefined) row.current_mileage = fields.mileage ? Number(fields.mileage) : null;
  if (fields.internalNotes !== undefined) row.internal_notes = fields.internalNotes || null;
  if (fields.distance_units !== undefined) row.distance_units = fields.distance_units || null;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase.from('vehicles').update(row).eq('id', vehicleId);
  if (error) throw error;
  console.log('[PM-Write] Synced vehicle update to Supabase:', vehicleId);
}

/**
 * Check if a vehicle has a photo (banner_photo) in Supabase.
 * Returns true if a non-null banner_photo exists, false otherwise.
 * Used by pushUnsyncedPhotos to avoid redundant writes.
 */
export async function vehicleHasPhotoInSupabase(vehicleId) {
  if (!vehicleId || String(vehicleId).startsWith('vehicle_')) return true; // skip local IDs
  const { data, error } = await supabase
    .from('vehicles')
    .select('banner_photo')
    .eq('id', vehicleId)
    .maybeSingle();
  if (error) throw error;
  return !!(data && data.banner_photo);
}

/** Check whether a garage already has a banner_photo in Supabase. */
export async function garageHasPhotoInSupabase(garageId) {
  if (!garageId || String(garageId).startsWith('garage_')) return true; // skip local IDs
  const { data, error } = await supabase
    .from('garages')
    .select('banner_photo')
    .eq('id', garageId)
    .maybeSingle();
  if (error) throw error;
  return !!(data && data.banner_photo);
}

/**
 * Sync garage field updates to Supabase.
 * THROWS on failure so callers can queue retries.
 * Skips garages with localStorage-style IDs (not yet synced).
 */
export async function updateGarageInSupabase(garageId, fields) {
  if (!garageId || String(garageId).startsWith('garage_')) return;
  const row = {};
  if (fields.name !== undefined) row.name = fields.name;
  if (fields.location !== undefined) row.location = fields.location;
  if (fields.photo !== undefined) row.banner_photo = fields.photo;
  if (fields.bannerPhoto !== undefined) row.banner_photo = fields.bannerPhoto;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase.from('garages').update(row).eq('id', garageId);
  if (error) throw error;
  console.log('[PM-Write] Synced garage update to Supabase:', garageId);
}

/**
 * Backfill garage_id on vehicles that have NULL garage_id in Supabase.
 * This happens when vehicles were created while the garage still had a
 * localStorage-generated ID (garage_xxx) — createVehicle skips setting
 * garage_id for those IDs.
 */
/**
 * Push a local vehicle to Supabase if it doesn't exist there yet.
 * Used to catch vehicles that were created while Supabase was unreachable.
 * Handles duplicate key gracefully (vehicle already exists).
 *
 * @param {object} vehicle - localStorage vehicle object
 * @param {string} userId - Supabase auth user ID
 * @returns {Promise<object|null>} - Created vehicle row or null if already exists
 */
export async function pushVehicleToSupabase(vehicle, userId) {
  if (!vehicle || !userId) return null;
  // Skip vehicles with localStorage-style IDs — they were never in Supabase
  // and we can't insert with a custom non-UUID id. We need to create fresh.
  const isLocalId = String(vehicle.id).startsWith('vehicle_');

  const row = {
    user_id: userId,
    year: vehicle.year ? Number(vehicle.year) : null,
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || null,
    current_mileage: vehicle.mileage ? Number(vehicle.mileage) : null,
    is_tmu: vehicle.tmu || false,
    estimated_mileage: null,
    condition: vehicle.condition || null,
    primary_use: Array.isArray(vehicle.primaryUse)
      ? vehicle.primaryUse
      : vehicle.primaryUse ? [vehicle.primaryUse] : [],
    banner_photo: vehicle.photo || null,
    distance_units: vehicle.distance_units || null,
  };

  // Set garage_id if it's a valid UUID
  if (vehicle.garageId && !String(vehicle.garageId).startsWith('garage_')) {
    row.garage_id = vehicle.garageId;
  }

  // If vehicle has a UUID (not a localStorage ID), try inserting with that ID
  if (!isLocalId) {
    row.id = vehicle.id;
  }

  const { data, error } = await supabase.from('vehicles').insert(row).select().single();

  // If duplicate key (already exists), that's fine
  if (error && (error.code === '23505' || error.message?.includes('duplicate'))) {
    console.log('[PM-Sync] Vehicle already exists in Supabase:', vehicle.id);
    return null;
  }
  if (error) throw error;

  console.log('[PM-Sync] Pushed vehicle to Supabase:', data.id, `(${vehicle.year} ${vehicle.make} ${vehicle.model})`);
  return data;
}

/**
 * Delete a vehicle from Supabase.
 * Skips vehicles with localStorage-style IDs (not in Supabase).
 */
export async function deleteVehicleFromSupabase(vehicleId) {
  if (!vehicleId || String(vehicleId).startsWith('vehicle_')) return;
  const { error } = await supabase.from('vehicles').delete().eq('id', vehicleId);
  if (error) throw error;
  console.log('[PM-Write] Deleted vehicle from Supabase:', vehicleId);
}

export async function backfillVehicleGarageIds(garageId, vehicleIds) {
  if (!garageId || String(garageId).startsWith('garage_') || !vehicleIds?.length) return;
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .update({ garage_id: garageId })
      .in('id', vehicleIds)
      .is('garage_id', null)
      .select('id');
    if (error) throw error;
    if (data?.length > 0) {
      console.log('[PM-Write] Backfilled garage_id for', data.length, 'vehicles');
    }
  } catch (err) {
    console.error('[PM-Write] Backfill failed:', err.message);
  }
}
